import { type InjectionKey, reactive, readonly, watchEffect } from "vue";
import {
  useRouter,
  useRoute,
  type RouteRecordName,
  type RouteParamValueRaw,
} from "vue-router";
import { provideLocal, injectLocal } from "@vueuse/core";

type HistoryEntry = string;
type GoBackFallbackParams = {
  name: RouteRecordName;
  params?: Record<string, RouteParamValueRaw>;
};

interface HistoryDefinition {
  history: HistoryEntry[];
  goBack: (fallbackParams?: GoBackFallbackParams) => void;
}

export const historyInjectionKey = Symbol(
  "History",
) as InjectionKey<HistoryDefinition>;

const MAX_HISTORY_ENTRIES = 50;

export function useSetupHistory() {
  const history = reactive<HistoryEntry[]>([]);
  const router = useRouter();
  const route = useRoute();

  watchEffect(() => {
    const currentPath = route.fullPath;
    const lastEntry = history[history.length - 1];

    if (currentPath !== lastEntry) {
      history.push(currentPath);
      if (history.length > MAX_HISTORY_ENTRIES) {
        history.splice(0, history.length - MAX_HISTORY_ENTRIES);
      }
    }
  });

  function goBack(fallbackParams?: GoBackFallbackParams) {
    // Try to use browser's native back functionality first
    if (window.history.state?.back != null) {
      try {
        router.back();
        return;
      } catch (error) {
        console.warn("router.back() failed, using fallback navigation", error);
      }
    }

    // Fallback to programmatic navigation if browser back is not available
    const name = fallbackParams?.name;
    if (!name) return;

    const params = fallbackParams?.params;
    const routeDetails = {
      name,
      params: params ?? {},
    };

    router.push(routeDetails);
  }

  const definition: HistoryDefinition = {
    history: readonly(history),
    goBack,
  };

  provideLocal(historyInjectionKey, definition);
  return definition;
}

export function useHistory() {
  const historyInstance = injectLocal(historyInjectionKey);
  if (!historyInstance) {
    throw new Error(
      "useHistory() was called without a parent (or self) calling useSetupHistory() first",
    );
  }
  return historyInstance;
}
